import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  SocialService, SocialAccount, SocialNetwork,
  SocialMediaType, SocialPostRequest, SocialPost
} from '../../../services/social.service';

@Component({
  selector: 'app-social-editor',
  templateUrl: './social-editor.component.html',
  styleUrls: ['./social-editor.component.scss']
})
export class SocialEditorComponent implements OnInit {

  // Modo
  editMode = false;
  postId: number | null = null;
  loading = false;
  saving = false;
  errorMessage: string | null = null;

  // Datos del formulario
  content = '';
  mediaPath: string | null = null;
  mediaType: SocialMediaType = 'NONE';
  mediaFilename = '';
  selectedNetworks: Set<SocialNetwork> = new Set();
  youtubeTitle = '';
  youtubeDescription = '';
  publishMode: 'now' | 'scheduled' = 'now';
  scheduledAt = '';

  // Generación IA
  showAiPanel = false;
  aiPrompt = '';
  aiTone = 'profesional';
  aiGenerating = false;

  // Cuentas disponibles
  connectedAccounts: SocialAccount[] = [];

  readonly networkLabels: { [key: string]: string } = {
    INSTAGRAM: 'Instagram', FACEBOOK: 'Facebook', LINKEDIN: 'LinkedIn', TIKTOK: 'TikTok',
    TWITTER: 'Twitter', YOUTUBE: 'YouTube'
  };

  constructor(
    private socialService: SocialService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.editMode = true;
      this.postId = +id;
      this.loadPost(+id);
    }
    this.loadAccounts();
    // hora por defecto: 1h desde ahora, redondeada a los 5 minutos
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
    this.scheduledAt = this.toDateTimeLocalString(d);
  }

  loadAccounts(): void {
    this.socialService.getAccounts().subscribe({
      next: (accounts) => this.connectedAccounts = accounts.filter(a => a.active)
    });
  }

  loadPost(id: number): void {
    this.loading = true;
    this.socialService.getPost(id).subscribe({
      next: (post) => {
        this.content = post.content || '';
        this.mediaPath = post.mediaPath || null;
        this.mediaType = post.mediaType;
        this.mediaFilename = post.mediaPath ? post.mediaPath.split('/').pop() || '' : '';
        this.selectedNetworks = new Set(post.targets.map(t => t.network));
        if (post.networkMetadata?.['YOUTUBE']) {
          this.youtubeTitle = post.networkMetadata['YOUTUBE']['title'] || '';
          this.youtubeDescription = post.networkMetadata['YOUTUBE']['description'] || '';
        }
        if (post.status === 'SCHEDULED') {
          this.publishMode = 'scheduled';
          this.scheduledAt = this.toDateTimeLocalString(new Date(post.scheduledAt));
        }
        this.loading = false;
      },
      error: () => {
        this.errorMessage = 'No se pudo cargar el post';
        this.loading = false;
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];

    this.errorMessage = null;
    this.saving = true;

    this.socialService.uploadMedia(file).subscribe({
      next: (res) => {
        this.mediaPath = res.mediaPath;
        this.mediaType = res.mediaType;
        this.mediaFilename = res.originalFilename;
        this.saving = false;
      },
      error: (err) => {
        this.errorMessage = 'Error subiendo archivo: ' + (err.error?.message || err.message);
        this.saving = false;
      }
    });
  }

  removeMedia(): void {
    this.mediaPath = null;
    this.mediaType = 'NONE';
    this.mediaFilename = '';
  }

  toggleNetwork(network: SocialNetwork): void {
    if (this.selectedNetworks.has(network)) {
      this.selectedNetworks.delete(network);
    } else {
      this.selectedNetworks.add(network);
    }
  }

  isNetworkSelected(network: SocialNetwork): boolean {
    return this.selectedNetworks.has(network);
  }

  isNetworkConnected(network: SocialNetwork): boolean {
    return this.connectedAccounts.some(a => a.network === network);
  }

  get isYoutubeSelected(): boolean {
    return this.selectedNetworks.has('YOUTUBE');
  }

  generateWithAi(): void {
    if (!this.aiPrompt.trim()) return;
    this.aiGenerating = true;
    this.socialService.generateText({
      prompt: this.aiPrompt,
      tone: this.aiTone,
      targetNetworks: Array.from(this.selectedNetworks)
    }).subscribe({
      next: (res) => {
        this.content = res.generatedText;
        this.aiGenerating = false;
        this.showAiPanel = false;
        this.aiPrompt = '';
      },
      error: (err) => {
        alert('Error generando texto: ' + (err.error?.message || err.message));
        this.aiGenerating = false;
      }
    });
  }

  save(): void {
    this.errorMessage = null;

    // Validaciones frontend
    if (this.selectedNetworks.size === 0) {
      this.errorMessage = 'Selecciona al menos una red social';
      return;
    }
    if (!this.content.trim() && !this.mediaPath) {
      this.errorMessage = 'El post debe tener texto, imagen o video';
      return;
    }
    if (this.selectedNetworks.has('INSTAGRAM') && !this.mediaPath) {
      this.errorMessage = 'Instagram requiere imagen o video';
      return;
    }
    if (this.selectedNetworks.has('YOUTUBE')) {
      if (this.mediaType !== 'VIDEO') {
        this.errorMessage = 'YouTube requiere un video';
        return;
      }
      if (!this.youtubeTitle.trim()) {
        this.errorMessage = 'YouTube requiere un título';
        return;
      }
    }

    const networkMetadata: any = {};
    if (this.isYoutubeSelected) {
      networkMetadata['YOUTUBE'] = {
        title: this.youtubeTitle,
        description: this.youtubeDescription || this.content
      };
    }

    const request: SocialPostRequest = {
      content: this.content,
      mediaPath: this.mediaPath || undefined,
      mediaType: this.mediaType,
      targetNetworks: Array.from(this.selectedNetworks),
      networkMetadata: Object.keys(networkMetadata).length ? networkMetadata : undefined,
      publishNow: this.publishMode === 'now',
      scheduledAt: this.publishMode === 'scheduled' ? this.scheduledAt : undefined
    };

    this.saving = true;
    const obs = this.editMode && this.postId
      ? this.socialService.updatePost(this.postId, request)
      : this.socialService.createPost(request);

    obs.subscribe({
      next: () => {
        this.saving = false;
        this.router.navigate(['/social']);
      },
      error: (err) => {
        this.errorMessage = err.error?.message || err.message;
        this.saving = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/social']);
  }

  private toDateTimeLocalString(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}

